import { useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { HiX, HiOutlineChatAlt2 } from "react-icons/hi";

import { Modal } from "MVVM/Components/Wrappers/Existing/modal";
import { FEEDBACK_SUBMIT } from "MVVM/GraphQL/mutations/feedbackSubmit";
import {
  FeedbackButton,
  ModalContainer,
  ModalHeader,
  HeaderTitle,
  HeaderSubtitle,
  CloseButton,
  ModalBody,
  TextArea,
  CharCount,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
} from "./styledComponents";

const MAX_CHARS = 1000;

/**
 * Uncontrolled by default, so the site header keeps its own trigger button.
 * The site nav drives it from the account panel instead: that panel unmounts
 * when it closes, so the modal has to live above it.
 */
const FeedbackModal = ({
  open,
  onOpenChange,
  hideTrigger,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
} = {}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setIsOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  const [message, setMessage] = useState("");
  const { pathname } = useLocation();

  const [submitFeedback, { loading }] = useMutation(FEEDBACK_SUBMIT);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setMessage("");
  }, [setIsOpen]);

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return;

    await submitFeedback({
      variables: {
        input: {
          message: message.trim(),
          pageUrl: pathname,
        },
      },
    });

    handleClose();
  }, [message, submitFeedback, pathname, handleClose]);

  return (
    <>
      {!hideTrigger && (
        <FeedbackButton onClick={() => setIsOpen(true)}>
          <HiOutlineChatAlt2 size={14} />
          Leave Feedback
        </FeedbackButton>
      )}

      <Modal
        isModalOpen={isOpen}
        setModalOpen={(open: boolean) => !open && handleClose()}
        handleModalClose={undefined}
        disableOutsideClose={false}
      >
        <ModalContainer>
          <ModalHeader>
            <div>
              <HeaderTitle>Submit Feedback</HeaderTitle>
              <HeaderSubtitle>
                We value your feedback! Please share your thoughts, suggestions,
                or any issues you've encountered. Your input helps us improve
                and create a better experience for you and the platform.
              </HeaderSubtitle>
            </div>
            <CloseButton onClick={handleClose}>
              <HiX size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think..."
              maxLength={MAX_CHARS}
              autoFocus
            />
            <CharCount $isOver={message.length >= MAX_CHARS}>
              {message.length}/{MAX_CHARS}
            </CharCount>
          </ModalBody>

          <ModalFooter>
            <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={!message.trim() || loading}
            >
              {loading ? "Sending..." : "Submit"}
            </PrimaryButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>
    </>
  );
};

export { FeedbackModal };
